import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useAuthenticatedUser, useWorkspace } from '@treams/treams-hooks';
import {
	ActivityIcon,
	ComposeTweetIcon,
	ContrastIcon,
	GridIcon,
	MagicWandIcon,
} from '@treams/treams-icons';
import { Breakpoint, Sidebar } from '@treams/treams-ui';

import EmployeesNav from './EmployeesNav';
import { useManagerMode } from './ManagerModeContext';
import ManagerModeSwitch from './ManagerModeSwitch';
import ManagerNav from './ManagerNav';
import { MobileNavAccount, MobileNavActions } from './MobileNav';
import NavHeader from './NavHeader';
import PersonalNav from './PersonalNav';
import UserNav from './UserNav';
import { appNavPane } from './appNavPane';
import SettingsNav from './settings/SettingsNav';

const Pane = styled(Flex)`
	box-sizing: border-box;
	flex-direction: column;
	flex: 1 1 0%;
	width: 100%;
	min-height: 0;
	overflow: hidden;
	padding: 6px ${(props) => props.theme.tokens.padding.control}px
		${(props) => props.theme.tokens.padding.container}px;

	@media screen and (max-width: ${Breakpoint.Medium}) {
		padding-top: 0px;
	}
`;

const Scroll = styled(Flex)`
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	overflow-x: hidden;
	overflow-y: auto;
	overscroll-behavior: contain;
`;

function LaunchNav() {
	const { t } = useTranslation();

	return (
		<>
			<Sidebar.Item icon={<GridIcon />} title="Workspaces" path="/settings/workspaces" />
			<Sidebar.Item
				icon={<MagicWandIcon />}
				title={t('settings.ai-workbench')}
				path="/settings/workbench"
			/>
			<Sidebar.Item
				icon={<ComposeTweetIcon />}
				title="Announcements"
				path="/settings/announcements"
			/>
			<Sidebar.Item icon={<ContrastIcon />} title="Modules" path="/settings/modules" />
			<Sidebar.Item
				icon={<ActivityIcon />}
				title={t('settings.analytics')}
				path="/settings/analytics"
			/>
		</>
	);
}

export default function AppNav(props: { onClose?: () => void }) {
	const location = useLocation();
	const workspace = useWorkspace();
	const managermode = useManagerMode();
	const viewer = useAuthenticatedUser();

	const pane = appNavPane({
		pathname: location.pathname,
		isLaunch: workspace.id === 'launch',
		managerMode: managermode.enabled,
		viewerId: viewer.id,
	});

	return (
		<Pane>
			<Box flexShrink={0}>
				<NavHeader onClose={props.onClose} />
				{(pane === 'personal' || pane === 'manager') && <ManagerModeSwitch />}
				<MobileNavActions />
			</Box>
			<Scroll>
				{pane === 'launch' && <LaunchNav />}
				{pane === 'personal' && <PersonalNav />}
				{pane === 'manager' && <ManagerNav />}
				{pane === 'user' && <UserNav userID={pathUserId(location.pathname)} />}
				{pane === 'settings' && <SettingsNav />}
				{pane === 'employees' && <EmployeesNav />}
			</Scroll>
			<MobileNavAccount />
		</Pane>
	);
}

function pathUserId(pathname: string) {
	return pathname.match(/^\/users\/([^/]+)/)?.[1] ?? '';
}
